import { parsePackageCount, parseVariantName } from "@/lib/pricing/variant-pack-math";

type NamedVariant = { id?: string; name?: string | null };
type PricedVariant = {
	quantityAvailable?: number | null;
	pricing?: { price?: { gross: { amount: number; currency: string } } | null } | null;
};

const names = new Intl.Collator("en", { numeric: true, sensitivity: "variant" });

function strength(name: string): { unit: string; amount: number } | null {
	const match = name.match(/(\d+(?:[.,]\d+)?)\s*(mg|µg|μg|ug|mcg|ml|iu)\b/i);
	if (!match) return null;
	const unit = match[2].toLowerCase();
	const micrograms = ["µg", "μg", "ug", "mcg"].includes(unit);
	return {
		unit: micrograms ? "mg" : unit,
		amount: Number(match[1].replace(",", ".")) / (micrograms ? 1000 : 1),
	};
}

/** Unknown strengths follow recognized strengths; unlike raw names, 5mg precedes 10mg. */
export function compareStrengthNames(a: string, b: string): number {
	const left = strength(a);
	const right = strength(b);
	if (left && right) return names.compare(left.unit, right.unit) || left.amount - right.amount;
	if (left) return -1;
	if (right) return 1;
	return names.compare(a, b);
}

export function comparePackageNames(a: string, b: string): number {
	return parsePackageCount(a) - parsePackageCount(b) || names.compare(a, b);
}

export function compareVariantOrder(a: NamedVariant, b: NamedVariant): number {
	const left = a.name ?? "";
	const right = b.name ?? "";
	return (
		compareStrengthNames(left, right) ||
		comparePackageNames(
			parseVariantName(left)?.packageName ?? left,
			parseVariantName(right)?.packageName ?? right,
		) ||
		names.compare(left, right) ||
		names.compare(a.id ?? "", b.id ?? "")
	);
}

/** Returns a copy: Saleor's source ordering is shared by other consumers. */
export function sortVariantsByStrengthAndPackage<T extends NamedVariant>(variants: readonly T[]): T[] {
	return [...variants].sort(compareVariantOrder);
}

/** A null/omitted quantity means stock is untracked, as in the existing card selector. */
export function isPurchasableVariant(variant: PricedVariant): boolean {
	const price = variant.pricing?.price?.gross;
	return (
		(variant.quantityAvailable == null || variant.quantityAvailable > 0) &&
		Boolean(price && Number.isFinite(price.amount) && price.amount >= 0 && price.currency)
	);
}
